'use client';

import type { ChangeEvent, ComponentPropsWithRef } from 'react';
import { forwardRef, useState } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

type InputProps = Omit<ComponentPropsWithRef<'input'>, 'size'> &
  VariantProps<typeof inputVariants> & {
    initialValue?: string;
  };

const inputVariants = tv(
  {
    base: 'flex items-center border-b border-gray-150 w-full text-base placeholder:text-sm placeholder:text-grey-600',
    variants: {
      size: {
        sm: 'p-1',
        md: 'p-2',
        lg: 'p-3',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
  {
    responsiveVariants: true,
  },
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ onChange, className, initialValue, size, ...props }, ref) => {
    const [value, setValue] = useState(initialValue ?? '');

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }

      setValue(event.target.value);
    };

    return (
      <input
        ref={ref}
        className={inputVariants({ size, className })}
        onChange={handleChange}
        value={value}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
